import { Fragment } from "react";
import { ChevronDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TASK_QUICK_FILTERS,
  getTaskQuickFilterLabel,
  type TaskQuickFilter,
} from "@/components/atomic-crm/tasks/taskQuickFilters";
import { cn } from "@/lib/utils";

const CountPill = ({ count, active }: { count: number; active: boolean }) => (
  <span
    className={cn(
      "inline-flex min-w-5 items-center justify-center rounded-sm px-1 text-2xs font-semibold tabular-nums",
      active
        ? "bg-primary text-primary-foreground"
        : "bg-muted text-muted-foreground",
    )}
  >
    {count}
  </span>
);

/** "Open tasks ▾ 2" switcher: open / due today / this week / overdue / completed. */
export const TaskQuickFilterMenu = ({
  value,
  onChange,
  counts,
}: {
  value: TaskQuickFilter;
  onChange: (value: TaskQuickFilter) => void;
  counts: Record<TaskQuickFilter, number>;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button type="button" variant="ghost" size="sm" className="gap-2 px-2">
        <ListFilter className="size-4" />
        <span className="font-semibold">{getTaskQuickFilterLabel(value)}</span>
        <CountPill count={counts[value]} active />
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      {TASK_QUICK_FILTERS.map((option) => (
        <Fragment key={option.value}>
          {option.value === "completed" ? <DropdownMenuSeparator /> : null}
          <DropdownMenuItem
            onSelect={() => onChange(option.value)}
            className={cn(
              "justify-between",
              option.value === value && "bg-accent font-medium",
            )}
          >
            {option.label}
            {counts[option.value] > 0 ? (
              <CountPill
                count={counts[option.value]}
                active={option.value === value}
              />
            ) : null}
          </DropdownMenuItem>
        </Fragment>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);
